import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Layout, Row, Col, Card, Typography, message, Drawer, Button, Space, Tooltip, ConfigProvider } from "antd";
import esES from "antd/locale/es_ES";
import "dayjs/locale/es";
import { PlusOutlined, ReloadOutlined, FilePdfOutlined, SaveOutlined } from "@ant-design/icons";
import { selectAppPermissions } from "../../../store/auth/selectors";
import {
  getActasPO, getStatsEntregasPO, getLineasActaPO, createActaPO, updateActaPO, deleteEntregaPO
} from "../../../services/financiero/ingresosPO/entregasPOService";
import {
  getMunicipiosPO, getDepartamentosPO, getProyectosPO, getMunicipiosPorProyectoPO, getResponsablesPO
} from "../../../services/financiero/ingresosPO/catalogosPOService";
import { getOrdenesServicioPO } from "../../../services/financiero/ingresosPO/ordenesServicioPOService";
import Header160 from "../../header/header";
import styles from "../ingresosPOStyles.module.css";
import EntregasPOCards from "./components/EntregasPOCards";
import EntregasPOFilters from "./components/EntregasPOFilters";
import EntregasPOTable from "./components/EntregasPOTable";
import EntregaActaForm from "./components/EntregaActaForm";
import { descargarActaPDF } from "./pdf/descargarActaPDF";

const { Content } = Layout;
const { Title, Text } = Typography;

export default function EntregasPOPage() {

  const appName = "entregas_ingresos_po";
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const user = useSelector((state) => state.auth.user);
  const userLogin = user?.username || "system";
  const permissionAccess = permissions?.priv_access === "Y";
  const permissionDelete = permissions?.priv_delete === "Y";
  const permissionInsert = permissions?.priv_insert === "Y";
  const permissionUpdate = permissions?.priv_update === "Y";

  const [loading, setLoading] = useState(false);
  const [actas, setActas] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filtros, setFiltros] = useState({});

  const [productos, setProductos] = useState([]);
  const [municipiosProyecto, setMunicipiosProyecto] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [catalogosListos, setCatalogosListos] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [acta, setActa] = useState({ isValid: false });
  // Acta que se está editando ({ record, lineas }); null al registrar una nueva.
  const [actaEdit, setActaEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const cargarActas = async (page = pagination.current, pageSize = pagination.pageSize, filtrosActuales = filtros) => {
    try {
      setLoading(true);
      const res = await getActasPO({ page, limit: pageSize, ...filtrosActuales });
      setActas(res.data || []);
      setPagination({ current: res.page, pageSize: res.limit, total: res.total });
    } catch (error) {
      console.error(error);
      message.error("Error cargando entregas");
    } finally {
      setLoading(false);
    }
  };

  const cargarStats = async (filtrosActuales = filtros) => {
    try {
      const res = await getStatsEntregasPO(filtrosActuales);
      setStats(res);
    } catch (error) {
      console.error(error);
    }
  };

  const cargarProductos = async () => {
    try {
      setProductos((await getOrdenesServicioPO()) || []);
    } catch (error) {
      console.error(error);
      message.error("Error cargando los productos");
    }
  };

  const cargarCatalogos = async () => {
    try {
      const [mun, dep, proy, munProy, resp] = await Promise.all([
        getMunicipiosPO(),
        getDepartamentosPO(),
        getProyectosPO(),
        getMunicipiosPorProyectoPO(),
        getResponsablesPO()
      ]);
      setMunicipios(mun || []);
      setDepartamentos(dep || []);
      setProyectos(proy || []);
      setMunicipiosProyecto(munProy || []);
      setResponsables(resp || []);
      await cargarProductos();
    } catch (error) {
      console.error(error);
      message.error("Error cargando catálogos");
    } finally {
      setCatalogosListos(true);
    }
  };

  // Al abrir el formulario se refresca el stock de los productos.
  const handleNuevaEntrega = () => {
    setActa({ isValid: false });
    setActaEdit(null);
    cargarProductos();
    setDrawerOpen(true);
  };

  const handleEditarActa = async (record) => {
    if (!permissionUpdate) {
      message.warning("No tiene permisos para editar");
      return;
    }
    try {
      setLoading(true);
      const [lineas] = await Promise.all([
        getLineasActaPO({
          oid_participante: record.oid_participante,
          tipo_entrega: record.tipo_entrega,
          fecha_entrega: record.fecha_entrega
        }),
        cargarProductos()
      ]);
      // Al guardar se eliminan las líneas que no estén en el formulario: si no
      // se pudieron cargar todas, no se deja editar para no perder productos.
      if (lineas.length !== Number(record.num_lineas)) {
        message.error("No se pudieron cargar todos los productos del acta; no es posible editarla desde aquí.");
        return;
      }
      setActa({ isValid: false });
      setActaEdit({ record, lineas });
      setDrawerOpen(true);
    } catch (error) {
      console.error(error);
      message.error("Error cargando el acta");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (permissionAccess) {
      cargarActas(1, pagination.pageSize, {});
      cargarStats({});
      cargarCatalogos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionAccess]);

  const handleTableChange = (paginationInfo) => {
    cargarActas(paginationInfo.current, paginationInfo.pageSize, filtros);
  };

  const handleFilter = (nuevosFiltros) => {
    setFiltros(nuevosFiltros);
    cargarActas(1, pagination.pageSize, nuevosFiltros);
    cargarStats(nuevosFiltros);
  };

  const handleRefrescar = () => {
    cargarActas();
    cargarStats(filtros);
  };

  const handleDeleteActa = async (record) => {
    try {
      const lineas = await getLineasActaPO({
        oid_participante: record.oid_participante,
        tipo_entrega: record.tipo_entrega,
        fecha_entrega: record.fecha_entrega
      });
      await Promise.all(lineas.map((l) => deleteEntregaPO(l.oid, userLogin)));
      message.success("Acta eliminada");
      cargarActas();
      cargarStats(filtros);
    } catch (error) {
      console.error(error);
      message.error("Error eliminando el acta");
    }
  };

  const handleGuardarActa = async () => {
    if (!acta.lineas?.length) {
      message.warning("Agregue al menos un producto");
      return;
    }
    if (!acta.isValid) {
      message.warning("Complete los campos obligatorios y verifique las cantidades");
      return;
    }
    if (actaEdit) {
      try {
        setSaving(true);
        const { record } = actaEdit;
        await updateActaPO({
          original: {
            oid_participante: record.oid_participante,
            tipo_entrega: record.tipo_entrega,
            fecha_entrega: record.fecha_entrega
          },
          encabezado: acta.encabezado,
          lineas: acta.lineas
        }, userLogin);
        message.success("Acta actualizada. Puede descargar el PDF desde la tabla.");
        setDrawerOpen(false);
        cargarActas();
        cargarStats(filtros);
        cargarProductos();
      } catch (error) {
        console.error(error);
        message.error(error?.response?.data?.message || "Error actualizando el acta");
        cargarProductos();
      } finally {
        setSaving(false);
      }
      return;
    }
    try {
      setSaving(true);
      await createActaPO({ encabezado: acta.encabezado, lineas: acta.lineas }, userLogin);
      message.success("Acta de entrega registrada");
      setDrawerOpen(false);
      cargarActas(1, pagination.pageSize, filtros);
      cargarStats(filtros);
      cargarProductos();
      // Al guardar se descarga de una vez el PDF del acta.
      try {
        await descargarActaPDF(acta.actaPDF, acta.lineasPDF, acta.extrasPDF);
      } catch (error) {
        console.error(error);
        message.warning("El acta se guardó, pero no se pudo generar el PDF. Puede descargarlo desde la tabla.");
      }
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.message || "Error guardando la entrega");
      // Si falló por stock, se refresca para mostrar las cantidades reales.
      cargarProductos();
    } finally {
      setSaving(false);
    }
  };

  if (!permissionAccess) {
    return (
      <Content style={{ padding: 24 }}>
        <Card>
          <Text>No tienes permisos para acceder a este módulo.</Text>
        </Card>
      </Content>
    );
  }

  return (
    <ConfigProvider locale={esES}>
    <div className={`container ${styles.ingresosPoModule}`}>
      <Content style={{ padding: 24 }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div className="header-filter-container d-flex align-items-center justify-content-between" style={{ flexWrap: "wrap", gap: 16 }}>
              <Header160 />
              <Col flex="auto" style={{ textAlign: "center", minWidth: 280 }}>
                <Space direction="vertical" size={2} align="center">
                  <Title level={4} style={{ margin: 0, color: "#042956", fontWeight: 600 }}>
                    Entregas de productos
                  </Title>
                  <Space wrap size={8} style={{ marginTop: 8 }}>
                    {permissionInsert && (
                      <Button type="primary" icon={<PlusOutlined />} onClick={handleNuevaEntrega}>
                        Nueva entrega
                      </Button>
                    )}
                    <Tooltip title="Refrescar">
                      <Button icon={<ReloadOutlined />} onClick={handleRefrescar} />
                    </Tooltip>
                  </Space>
                </Space>
              </Col>
            </div>
          </Col>
          <Col span={24}>
            <EntregasPOCards stats={stats} />
          </Col>
          <Col span={24}>
            <EntregasPOFilters
              proyectos={proyectos}
              departamentos={departamentos}
              municipios={municipios}
              onFilter={handleFilter}
            />
          </Col>
          <Col span={24}>
            <EntregasPOTable
              data={actas}
              pagination={pagination}
              onTableChange={handleTableChange}
              loading={loading}
              onDeleteActa={handleDeleteActa}
              onEditActa={handleEditarActa}
              onDatosActaGuardados={() => cargarActas()}
              permissionDelete={permissionDelete}
              permissionUpdate={permissionUpdate}
              userLogin={userLogin}
            />
          </Col>
        </Row>

        <Drawer
          title={actaEdit ? `Editar acta de entrega — ${actaEdit.record.nombre_entrega || ""}` : "Nueva acta de entrega"}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width="100vw"
          rootClassName={styles.ingresosPoModule}
          maskClosable={false}
          destroyOnClose
          extra={
            <Space>
              <Button onClick={() => setDrawerOpen(false)}>Cancelar</Button>
              {actaEdit ? (
                <Tooltip title="Guarda los cambios del acta">
                  <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleGuardarActa}>
                    Guardar cambios
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip title="Guarda la entrega y descarga el PDF del acta">
                  <Button type="primary" icon={<FilePdfOutlined />} loading={saving} onClick={handleGuardarActa}>
                    Guardar y descargar acta
                  </Button>
                </Tooltip>
              )}
            </Space>
          }
        >
          {catalogosListos ? (
            <EntregaActaForm
              proyectos={proyectos}
              municipiosProyecto={municipiosProyecto}
              productos={productos}
              responsables={responsables}
              actaEdit={actaEdit}
              onChange={setActa}
            />
          ) : (
            <div style={{ textAlign: "center", padding: 60 }}>Cargando catálogos…</div>
          )}
        </Drawer>
      </Content>
    </div>
    </ConfigProvider>
  );
}
